import { useEffect, useId, useRef, useState } from "react";
import clsx from "clsx";
import { Button } from "@marekl/adminer/components";
import styles from "./BarChart.module.css";

export interface BarSeries {
  label: string;
  /** One value per category, in the categories' order. */
  values: number[];
  /** A CSS colour; tokens from tokens.css (--faktio-chart-*). */
  color: string;
}

export interface BarChartProps {
  /** Names the chart for screen readers and the table's caption. */
  title: string;
  /** Short labels under the axis ("1"…"12"), and full ones for tooltips and the table. */
  categories: { short: string; long: string }[];
  series: BarSeries[];
  /** Formats a value for the tooltip, the table and the axis. */
  format: (v: number) => string;
  /** Formats an axis tick, shorter than `format` ("12 tis."). */
  formatTick?: (v: number) => string;
  /** The axis an all-zero chart spans, so it still shows a few round ticks. */
  minSpan?: number;
}

const H = 240;
const PAD = { top: 12, right: 8, bottom: 26, left: 56 };
const BAR_GAP = 2;
/** The tooltip's half width and height (CSS: min-width 180px), for keeping it inside the card. */
const TIP_HALF_W = 100;
const TIP_H = 80;

/** Round tick step for a range: 1, 2 or 5 × 10ⁿ, about four lines. */
function niceStep(range: number): number {
  if (range <= 0) return 1;
  const raw = range / 4;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const f = raw / pow;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10) * pow;
}

/**
 * Grouped bars, one group per category, plain SVG (docs/R1_PLAN.md batch 1b:
 * no chart library). Each bar has a hover/focus tooltip; the legend names the
 * series and a table view holds every value, so nothing depends on colour or
 * hovering. Negative values (a month of credit notes) hang below the zero line.
 */
export function BarChart({ title, categories, series, format, formatTick = format, minSpan = 1 }: BarChartProps) {
  const id = useId();
  const [hover, setHover] = useState<{ cat: number; s: number } | null>(null);
  const [table, setTable] = useState(false);
  // Drawn at the container's width, so text keeps its size on a phone and
  // only the bars narrow.
  const plot = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(720);
  useEffect(() => {
    const el = plot.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setW(Math.max(280, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, [table]);
  const all = series.flatMap((s) => s.values);
  const step = niceStep(Math.max(Math.max(...all, 0) - Math.min(...all, 0), minSpan));
  // An empty chart spans minSpan, so it shows a few round ticks.
  const top = Math.max(step, Math.ceil(Math.max(...all, 0) / step) * step, all.every((v) => v === 0) ? Math.ceil(minSpan / step) * step : 0);
  const bottom = Math.min(0, Math.floor(Math.min(...all, 0) / step) * step);
  const y = (v: number) => PAD.top + ((top - v) / (top - bottom)) * (H - PAD.top - PAD.bottom);
  const ticks: number[] = [];
  for (let t = bottom; t <= top + step / 2; t += step) ticks.push(t);
  const groupW = (W - PAD.left - PAD.right) / categories.length;
  const barW = Math.min(18, (groupW * 0.7 - BAR_GAP * (series.length - 1)) / series.length);
  const groupInner = barW * series.length + BAR_GAP * (series.length - 1);
  const x = (cat: number, s: number) => PAD.left + cat * groupW + (groupW - groupInner) / 2 + s * (barW + BAR_GAP);

  // Above the bar, or below its top when there is no room above; never
  // past the card's sides (the card clips).
  const tip = hover && (() => {
    const cx = x(hover.cat, hover.s) + barW / 2;
    const barTop = y(Math.max(0, series[hover.s].values[hover.cat]));
    const below = barTop < TIP_H;
    return {
      left: Math.min(Math.max(cx, TIP_HALF_W), W - TIP_HALF_W),
      top: below ? barTop + 8 : barTop - 8,
      below,
    };
  })();

  return (
    <figure className={styles.figure} aria-labelledby={`${id}-title`}>
      <figcaption className={styles.head}>
        <span id={`${id}-title`} className={styles.srOnly}>{title}</span>
        <ul className={styles.legend}>
          {series.map((s) => (
            <li key={s.label}>
              <span className={styles.swatch} style={{ background: s.color }} aria-hidden="true" />
              {s.label}
            </li>
          ))}
        </ul>
        <Button type="button" size="sm" weight="ghost" className={styles.toggle} pressed={table} onClick={() => setTable((t) => !t)}>
          {table ? "graf" : "tabulka"}
        </Button>
      </figcaption>

      {table ? (
        <table className={styles.table}>
          <caption className={styles.srOnly}>{title}</caption>
          <thead>
            <tr>
              <th scope="col" />
              {series.map((s) => <th key={s.label} scope="col">{s.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {categories.map((c, i) => (
              <tr key={c.long}>
                <th scope="row">{c.long}</th>
                {series.map((s) => <td key={s.label}>{format(s.values[i])}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className={styles.plot} ref={plot}>
          <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} className={styles.svg} role="group" aria-label={title}>
            {ticks.map((t) => (
              <g key={t}>
                <line x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)} className={t === 0 ? styles.zero : styles.grid} />
                <text x={PAD.left - 8} y={y(t)} className={styles.tick} textAnchor="end" dominantBaseline="middle">
                  {formatTick(t)}
                </text>
              </g>
            ))}
            {categories.map((c, ci) => (
              <g key={c.long}>
                {series.map((s, si) => {
                  const v = s.values[ci];
                  const y0 = y(0);
                  const y1 = y(v);
                  const h = Math.abs(y1 - y0);
                  const r = Math.min(4, barW / 2, h);
                  // Rounded at the data end, square on the zero line.
                  const xl = x(ci, si);
                  const xr = xl + barW;
                  const d =
                    v >= 0
                      ? `M${xl},${y0} V${y1 + r} Q${xl},${y1} ${xl + r},${y1} H${xr - r} Q${xr},${y1} ${xr},${y1 + r} V${y0} Z`
                      : `M${xl},${y0} V${y1 - r} Q${xl},${y1} ${xl + r},${y1} H${xr - r} Q${xr},${y1} ${xr},${y1 - r} V${y0} Z`;
                  const active = hover?.cat === ci && hover.s === si;
                  return (
                    <g key={s.label}>
                      {h > 0 && <path d={d} fill={s.color} className={clsx(styles.bar, hover && !active && styles.dim)} />}
                      {/* The hit target: the whole column height of the bar's slot. */}
                      <rect
                        x={xl - BAR_GAP / 2}
                        y={PAD.top}
                        width={barW + BAR_GAP}
                        height={H - PAD.top - PAD.bottom}
                        className={styles.hit}
                        tabIndex={0}
                        aria-label={`${c.long}, ${s.label}: ${format(v)}`}
                        onMouseEnter={() => setHover({ cat: ci, s: si })}
                        onMouseLeave={() => setHover(null)}
                        onFocus={() => setHover({ cat: ci, s: si })}
                        onBlur={() => setHover(null)}
                      />
                    </g>
                  );
                })}
                <text x={PAD.left + ci * groupW + groupW / 2} y={H - 8} className={styles.tick} textAnchor="middle">
                  {c.short}
                </text>
              </g>
            ))}
          </svg>
          {hover && tip && (
            // The focused bar's own label carries the value for assistive tech.
            <div className={clsx(styles.tooltip, tip.below && styles.below)} style={{ left: tip.left, top: tip.top }} aria-hidden="true">
              <div className={styles.tipTitle}>{categories[hover.cat].long}</div>
              {series.map((s, si) => (
                <div key={s.label} className={clsx(styles.tipRow, si === hover.s && styles.tipActive)}>
                  <span className={styles.swatch} style={{ background: s.color }} aria-hidden="true" />
                  <span>{s.label}</span>
                  <b>{format(s.values[hover.cat])}</b>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </figure>
  );
}
